import { apiFetch } from './http';

// ─────────────────────────────────────────────────────────────────────
// «SEND BETALINGSLENKE» — the browser half of
// `POST /organization/bookings/:id/payment-link` (#1137).
//
// Staff name the member a priced staff booking is for; the server makes that
// member its owner, holds it for payment and mails them a link. The request
// carries the booking's `book_…` id and the member's roster id (the player's
// legacy id, as `GET /members/roster` hands it out) — nothing about money: the
// server prices the booking itself. The answer is a CODE; the screen decides
// the sentence.
// ─────────────────────────────────────────────────────────────────────

/** How the member was told, as the server reports it. */
export type PaymentLinkMail =
  'sent' | 'not_configured' | 'no_address' | 'refused' | 'provider_error';

export type SendPaymentLinkResult =
  /** Linked. The booking is payable in the member's app whatever `mail` says. */
  | {
      readonly state: 'linked';
      readonly playerName: string;
      readonly amountMinor: number;
      readonly mail: PaymentLinkMail;
    }
  /** Refused, with the server's code (`price_mismatch`, `not_on_roster`, …). */
  | { readonly state: 'refused'; readonly code: string }
  /** The request never got an answer we understand. Nothing is known. */
  | { readonly state: 'error'; readonly status: number };

const MAILS: readonly PaymentLinkMail[] = [
  'sent',
  'not_configured',
  'no_address',
  'refused',
  'provider_error',
];

export async function sendPaymentLink(
  bookingId: string,
  playerId: string,
): Promise<SendPaymentLinkResult> {
  let res: Response;
  try {
    res = await apiFetch(`/organization/bookings/${encodeURIComponent(bookingId)}/payment-link`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ playerId }),
    });
  } catch {
    return { state: 'error', status: 0 };
  }
  const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
  if (res.status === 200 && body?.linked === true) {
    const mail = MAILS.includes(body.mail as PaymentLinkMail)
      ? (body.mail as PaymentLinkMail)
      : 'provider_error';
    return {
      state: 'linked',
      playerName: typeof body.playerName === 'string' ? body.playerName : '',
      amountMinor: typeof body.amountMinor === 'number' ? body.amountMinor : 0,
      mail,
    };
  }
  if (typeof body?.error === 'string') return { state: 'refused', code: body.error };
  return { state: 'error', status: res.status };
}

// ─────────────────────────────────────────────────────────────────────
// «TREKK TILBAKE BETALINGSLENKE» — `DELETE …/payment-link` (#1310).
//
// The booking goes back to the club: the server retires any open payment at
// the provider first, clears the member as owner and keeps the hold. As for
// sending, the answer is a CODE and the screen decides the sentence.
// ─────────────────────────────────────────────────────────────────────

export type WithdrawPaymentLinkResult =
  /** Withdrawn. `attemptCancelled`: an open payment was cancelled on the way. */
  | { readonly state: 'withdrawn'; readonly attemptCancelled: boolean }
  /** Refused, with the server's code (`not_linked`, `payment_unconfirmed`, …). */
  | { readonly state: 'refused'; readonly code: string }
  | { readonly state: 'error'; readonly status: number };

export async function withdrawPaymentLink(bookingId: string): Promise<WithdrawPaymentLinkResult> {
  let res: Response;
  try {
    res = await apiFetch(`/organization/bookings/${encodeURIComponent(bookingId)}/payment-link`, {
      method: 'DELETE',
    });
  } catch {
    return { state: 'error', status: 0 };
  }
  const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
  if (res.status === 200 && body?.withdrawn === true) {
    return { state: 'withdrawn', attemptCancelled: body.attemptCancelled === true };
  }
  if (typeof body?.error === 'string') return { state: 'refused', code: body.error };
  return { state: 'error', status: res.status };
}
